// src/modules/app-views/crm/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import CrmBoards from './pages/Boards';
import CrmKanban from './pages/Kanban';
import CrmDocument from './pages/Document';
import DocumentProvider from 'components/core/DocumentProvider';
import { API } from 'constants/Api';
import crmInitActionButton from 'modules/app-views/crm/pages/Boards/actions/actionButton';
import crmInitMenuButton from 'modules/app-views/crm/pages/Boards/actions/menuButton';
import crmKanbanActionButton from 'modules/app-views/crm/pages/Kanban/actions/actionButton';
import crmKanbanMenuButton from 'modules/app-views/crm/pages/Kanban/actions/menuButton';
import crmKanbanNewOpportunityButton from 'modules/app-views/crm/pages/Kanban/actions/newOpportunityButton';
import crmDocumentActionButton from './pages/Document/actions/actionButton';
import crmDocumentRemoveButton from './pages/Document/actions/removeButton';
import crmDocumentDecorateRequest from './pages/Document/decorators/request';
import CrmParametersModal from 'modules/app-views/crm/pages/Boards/modals/CrmParametersModal';
import useZustand from 'hooks/useZustand';
import { fromAppStore } from 'stores/app';
import seedCrmParameters from './seed';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import {
  documentCodeItem,
  createdAtItem,
  updatedAtItem,
} from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import CrmCardHeaderSubtitle from './components/CardHeaderSubtitle';
import CrmCardHeaderColumn2 from './components/CardHeaderColumn2';
import CrmCardHeaderTags from './components/CardHeaderTags';
import CrmCardHeaderBottom from './components/CardHeaderBottom';
import CrmCardHeaderIcons from './components/CardHeaderIcons';
import PageProvider from 'components/core/PageProvider';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'crm';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'boards',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: CrmParametersModal,
  parametersSeed: seedCrmParameters,
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  // ═══════════════════════════════════════════════════════════════════════════════
  // INIT PAGE - Boards/funnels overview
  // ═══════════════════════════════════════════════════════════════════════════════
  boards: {
    // ─────────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ─────────────────────────────────────────────────────────────────────────
    layout: 'page',
    moduleId: MODULE_ID,
    showMyTasksLink: true,
    component: props => <CrmBoards />,

    // ─────────────────────────────────────────────────────────────────────────
    // TOUR
    // ─────────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // API & DATA
    // ─────────────────────────────────────────────────────────────────────────
    api: {
      url: null,
      params: null,
      query: null,
      returnAsObject: true,
      autoFetch: false,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: false,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ─────────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ─────────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // LIST / TABLE (not used for this page)
    // ─────────────────────────────────────────────────────────────────────────
    pagination: null,
    table: null,

    // ─────────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER SETTINGS
    // ─────────────────────────────────────────────────────────────────────────
    settings: {
      // actions: ['return'],
      actions: null,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ─────────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: [
        // {
        //   action: 'refresh',
        //   disabled: false,
        //   onClick: () => {
        //     const appState = useZustand('app', 'state', false);
        //     if (typeof appState?.refreshParameters === 'function') {
        //       appState.refreshParameters();
        //     }
        //   },
        // },
        crmInitActionButton(),
        crmInitMenuButton(),
        // 'return',
      ],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['commercial', 'crm'],
      },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // KANBAN PAGE - Drag-drop board
  // ═══════════════════════════════════════════════════════════════════════════════
  kanban: {
    highSecurity: false,

    // ─────────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ─────────────────────────────────────────────────────────────────────────
    layout: 'page',
    moduleId: MODULE_ID,
    showMyTasksLink: true,
    component: props => (
      <PageProvider {...props}>
        <CrmKanban />
      </PageProvider>
    ),

    // ─────────────────────────────────────────────────────────────────────────
    // TOUR
    // ─────────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // API & DATA
    // ─────────────────────────────────────────────────────────────────────────
    api: {
      url: null,
      params: null,
      query: null,
      returnAsObject: true,
      autoFetch: false,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: false,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ─────────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ─────────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/board/:boardUid',
      paramKey: 'boardUid',
      query: null,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // LIST / TABLE (not used)
    // ─────────────────────────────────────────────────────────────────────────
    pagination: null,
    table: null,

    // ─────────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER SETTINGS
    // ─────────────────────────────────────────────────────────────────────────
    settings: {
      // 'return' here is required by GoBackButton which reads settings.actions
      actions: ['return'],
      backPage: 'boards',
    },

    // ─────────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ─────────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: [
        {
          action: 'refresh',
          disabled: false,
          onClick: () => {
            window.dispatchEvent(new CustomEvent('crm:kanban:refresh'));
          },
        },
        crmKanbanNewOpportunityButton(),
        crmKanbanActionButton(),
        crmKanbanMenuButton(),
        'return',
      ],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => {
          const boardUid = getBoardUidFromUrl();
          const boardTitle = getBoardTitle(boardUid);
          return boardTitle ? ['commercial', 'crm', boardTitle] : ['commercial', 'crm'];
        },
      },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // DOCUMENT PAGE - Opportunity form
  // ═══════════════════════════════════════════════════════════════════════════════
  document: {
    highSecurity: false,

    // ─────────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ─────────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    showMyTasksLink: true,
    component: props => (
      <DocumentProvider {...props}>
        <CrmDocument />
      </DocumentProvider>
    ),

    // ─────────────────────────────────────────────────────────────────────────
    // TOUR
    // ─────────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // API & DATA
    // ─────────────────────────────────────────────────────────────────────────
    api: {
      url: API.CRM.URL,
      params: null,
      query: null,
      returnAsObject: true,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ─────────────────────────────────────────────────────────────────────────
    decorators: {
      request: crmDocumentDecorateRequest,
      response: null,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ─────────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // LIST / TABLE (not used)
    // ─────────────────────────────────────────────────────────────────────────
    pagination: null,
    table: null,

    // ─────────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER SETTINGS
    // ─────────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {
        status: 'backlog',
        opportunityType: 'sale',
      },
      actions: [crmDocumentRemoveButton(), crmDocumentActionButton(), 'return'],
      viewMode: 'auto',
      // Open in edit mode so DocumentProvider.viewMode stays 'edit';
      // actual field editability is controlled by effectiveViewMode (locked prop) in CrmDocument
      viewModeOnOpen: 'edit',
      viewModeAfterSave: 'edit',
    },

    // ─────────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ─────────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: ({ data }) => {
          const boardTitle = getBoardTitle(data?.boardUid);
          return boardTitle
            ? ['commercial', 'crm', boardTitle, 'opportunity']
            : ['commercial', 'crm', 'opportunity'];
        },
      },

      card: {
        title: props => (
          <CardHeaderTitle
            name={props?.opportunity}
            placeholder="new_opportunity"
          />
        ),
        subtitle: props => <CrmCardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn {...props} items={[documentCodeItem, createdAtItem, updatedAtItem]} />
        ),
        column2: props => <CrmCardHeaderColumn2 {...props} />,
        column3: null,
        tags: props => <CrmCardHeaderTags {...props} />,
        icons: props => <CrmCardHeaderIcons {...props} />,
        bottom: props => props?.boardUid ? <CrmCardHeaderBottom {...props} /> : null,
      },
    },
  },
};

// *******************************************************************************
// HELPERS — dynamic breadcrumbs
// *******************************************************************************

// Look up a CRM board title from stored parameters (usable outside React components)
function getBoardTitle(boardUid) {
  if (!boardUid) return null;
  const boards = fromAppStore(s => s.parameters?.modules?.crm?.boards) || [];
  const board = boards.find(b => b.uid === boardUid);
  return board?.title || null;
}

// Extract boardUid from the current URL path (/…/board/:boardUid)
function getBoardUidFromUrl() {
  const match = window.location.pathname.match(/\/board\/([^/]+)/);
  return match?.[1] || null;
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
